import type { Paso } from '@domain/rules/cuentas.ts'

/**
 * Lo que dice cada paso de una cuenta.
 *
 * El dominio produce los pasos como datos —qué columna, qué cifras, cuánto se
 * lleva— y aquí se les pone la voz: la del maestro que explica en la pizarra,
 * de tú, sin adornos. Vive aparte de `es.ts` porque son plantillas sobre un
 * paso y no cadenas sueltas, pero está bajo el mismo guardián: `pasos.test.ts`
 * genera cientos de cuentas, saca todos sus textos y les pasa exactamente las
 * mismas reglas que a la interfaz, incluido el límite de 120 caracteres. Uno de
 * los dos niños lee de forma literal, y esto se lee en voz alta más que nada.
 *
 * Tres textos por paso:
 *
 * - **pregunta**: lo que se le pide cuando el paso lo hace él. Solo en los
 *   pasos que tienen respuesta.
 * - **explica**: el paso ya hecho, con su resultado. Es lo que se ve en el
 *   ejemplo resuelto y en los pasos que la ayuda le hace.
 * - **pistas**: dos, de lo general a lo concreto. La primera dice cómo se
 *   piensa; la segunda casi lo hace. La respuesta no es una pista: se enseña
 *   sola después de dos intentos.
 */

export interface TextoPaso {
  readonly pregunta?: string
  readonly explica: string
  readonly pistas: readonly string[]
}

function mayuscula(texto: string): string {
  return texto.charAt(0).toUpperCase() + texto.slice(1)
}

const n = (x: number): string => String(x)

export function textoDelPaso(p: Paso): TextoPaso {
  switch (p.tipo) {
    case 'alinear':
      return {
        explica: p.ceros
          ? 'Pongo una coma debajo de la otra, y un cero donde falte para que tengan los mismos decimales.'
          : 'Pongo una coma debajo de la otra, para que cada cifra quede en su columna.',
        pistas: [],
      }

    case 'sumar-columna': {
      const L = mayuscula(p.lugar)
      const llev = p.llevada > 0 ? `, y ${n(p.llevada)} que me llevaba` : ''
      const cierre =
        p.seLleva > 0
          ? `Escribo el ${n(p.escribe)} y me llevo ${n(p.seLleva)}.`
          : `Escribo ${n(p.escribe)}.`
      const mayor = Math.max(p.a, p.b)
      const menor = Math.min(p.a, p.b)
      return {
        pregunta: `${L}: ${n(p.a)} + ${n(p.b)}${llev}. ¿Cuánto?`,
        explica: `${L}: ${n(p.a)} + ${n(p.b)}${llev} son ${n(p.suma)}. ${cierre}`,
        pistas: [
          p.llevada > 0
            ? `Primero ${n(p.a)} + ${n(p.b)}, y a eso le sumas ${n(p.llevada)}.`
            : `Empieza en ${n(mayor)} y cuenta ${n(menor)} más.`,
          `${n(p.a)} + ${n(p.b)} = ${n(p.a + p.b)}${p.llevada > 0 ? `. Y ${n(p.llevada)} más.` : '.'}`,
        ],
      }
    }

    case 'restar-columna': {
      const L = mayuscula(p.lugar)
      const abajo =
        p.llevada > 0 ? `${n(p.abajo)} y ${n(p.llevada)} que me llevaba son ${n(p.quita)}. ` : ''
      const van = p.puede
        ? `De ${n(p.quita)} a ${n(p.arriba)}`
        : `De ${n(p.quita)} a ${n(p.arriba)} no se puede, así que cojo ${n(p.tomo)}. De ${n(p.quita)} a ${n(p.tomo)}`
      const cierre =
        p.seLleva > 0 ? `Escribo ${n(p.resultado)} y me llevo 1.` : `Escribo ${n(p.resultado)}.`
      return {
        pregunta: `${L}: ${abajo}${van}, ¿cuántas van?`,
        explica: `${L}: ${abajo}${van} van ${n(p.resultado)}. ${cierre}`,
        pistas: [
          `Es lo mismo que ${n(p.tomo)} menos ${n(p.quita)}.`,
          `Cuenta desde ${n(p.quita)} hasta ${n(p.tomo)}: son ${n(p.tomo - p.quita)} pasos.`,
        ],
      }
    }

    case 'bajar-coma':
      return { explica: 'Bajo la coma al resultado, en la misma columna.', pistas: [] }

    case 'quitar-comas':
      return {
        explica: `Quito las comas y multiplico ${p.sinA} × ${p.sinB} como si fueran enteros. Al final devuelvo la coma.`,
        pistas: [],
      }

    case 'multiplicar-cifra': {
      const L = mayuscula(p.lugar)
      const llev = p.llevada > 0 ? `, y ${n(p.llevada)} que me llevaba` : ''
      const cierre =
        p.seLleva > 0
          ? `Escribo el ${n(p.escribe)} y me llevo ${n(p.seLleva)}.`
          : `Escribo ${n(p.escribe)}.`
      return {
        pregunta: `${L}: ${n(p.cifra)} × ${n(p.por)}${llev}. ¿Cuánto?`,
        explica: `${L}: ${n(p.cifra)} × ${n(p.por)}${llev} son ${n(p.producto)}. ${cierre}`,
        pistas: [
          `Tabla del ${n(p.cifra)}: ${n(p.cifra)} × ${n(p.por)}.${p.llevada > 0 ? ` Luego súmale ${n(p.llevada)}.` : ''}`,
          `${n(p.cifra)} × ${n(p.por)} = ${n(p.cifra * p.por)}${p.llevada > 0 ? `, y ${n(p.llevada)} más.` : '.'}`,
        ],
      }
    }

    case 'nueva-fila':
      return {
        explica: `Ahora multiplico por el ${n(p.cifra)}, que son ${p.lugar}: fila nueva, ${
          p.desplaza === 1 ? 'un sitio' : `${n(p.desplaza)} sitios`
        } más a la izquierda.`,
        pistas: [],
      }

    case 'sumar-filas': {
      const cuenta = p.sumandos.join(' + ')
      return {
        pregunta: `Sumo las filas: ${cuenta}. ¿Cuánto?`,
        explica: `Sumo las filas: ${cuenta} = ${p.total}.`,
        pistas: [
          'Súmalas en columna, empezando por las unidades.',
          `${cuenta}: empieza por la derecha y llévate lo que pase de 9.`,
        ],
      }
    }

    case 'poner-coma': {
      const cuantas = p.decimales === 1 ? 'un decimal' : `${n(p.decimales)} decimales`
      return {
        pregunta: `Sin coma sale ${p.sinComa}. Entre los dos números había ${cuantas}. ¿Cómo queda con la coma?`,
        explica: `Sin coma sale ${p.sinComa}. Cuento ${cuantas} desde la derecha y pongo la coma: ${p.resultado}.`,
        pistas: [
          `Cuenta ${n(p.decimales)} ${p.decimales === 1 ? 'cifra' : 'cifras'} desde la derecha de ${p.sinComa} y pon la coma ahí.`,
          p.decimales === 1
            ? `La última cifra de ${p.sinComa} queda detrás de la coma.`
            : `Las ${n(p.decimales)} últimas cifras de ${p.sinComa} quedan detrás de la coma.`,
        ],
      }
    }

    case 'div-quitar-coma':
      return {
        explica: `El divisor tiene coma. Multiplico los dos por ${n(p.por)}: ${p.a} : ${p.b} es lo mismo que ${p.nuevoA} : ${p.nuevoB}.`,
        pistas: [],
      }

    case 'div-empezar': {
      const cifras = p.cifras === 1 ? 'una cifra' : `${n(p.cifras)} cifras`
      if (p.ceroYComa) {
        const ceros =
          p.cerosAnadidos === 0
            ? ''
            : p.cerosAnadidos === 1
              ? ', añadiendo un cero'
              : `, añadiendo ${n(p.cerosAnadidos)} ceros`
        return {
          pregunta: `El entero no llega a ${n(p.divisor)}: escribo «0,» en el cociente y sigo cogiendo cifras. ¿Con qué número empiezo?`,
          explica: `El entero no llega a ${n(p.divisor)}: escribo «0,» en el cociente y cojo hasta ${n(p.numero)}${ceros}.`,
          pistas: [
            `Sigue cogiendo cifras, o ceros, hasta que el número llegue a ${n(p.divisor)}.`,
            `Con ${cifras} llega: ${n(p.numero)} ya es mayor o igual que ${n(p.divisor)}.`,
          ],
        }
      }
      return {
        pregunta: `Cojo cifras del dividendo hasta tener un número donde quepa el ${n(p.divisor)}. ¿Con qué número empiezo?`,
        explica: `Cojo ${cifras}: ${n(p.numero)}, que ya es mayor o igual que ${n(p.divisor)}.`,
        pistas: [
          `Empieza por la primera cifra. Si es menor que ${n(p.divisor)}, coge una más.`,
          `Con ${cifras} ya llega.`,
        ],
      }
    }

    case 'div-cabe':
      return {
        pregunta: `¿Cuántas veces cabe el ${n(p.divisor)} en ${n(p.en)}?`,
        explica: `El ${n(p.divisor)} en ${n(p.en)} cabe ${n(p.veces)} veces: ${n(p.divisor)} × ${n(p.veces)} = ${n(p.producto)}. Escribo ${n(p.veces)} en el cociente.`,
        pistas: [
          p.divisor <= 10
            ? `Busca en la tabla del ${n(p.divisor)} el número más cercano a ${n(p.en)} sin pasarse.`
            : `Ve probando ${n(p.divisor)} × 2, × 3, × 4… hasta acercarte a ${n(p.en)} sin pasarte.`,
          `${n(p.divisor)} × ${n(p.veces + 1)} = ${n(p.siguiente)}, que se pasa de ${n(p.en)}. Prueba con uno menos.`,
        ],
      }

    case 'div-resto': {
      if (p.baja !== undefined && p.queda !== undefined) {
        const resta =
          p.producto === 0
            ? `No resto nada, porque no cabía.`
            : `Resto: de ${n(p.producto)} a ${n(p.en)}.`
        return {
          pregunta: `${resta} Y bajo el ${p.baja}. ¿Qué número me queda?`,
          explica:
            p.producto === 0
              ? `No resto nada. Bajo el ${p.baja} y me queda ${n(p.queda)}.`
              : `De ${n(p.producto)} a ${n(p.en)} van ${n(p.resto)}. Escribo ${n(p.resto)} y bajo el ${p.baja}: ${n(p.queda)}.`,
          pistas: [
            `De ${n(p.producto)} a ${n(p.en)} van ${n(p.resto)}. Ahora baja el ${p.baja} y pónselo al lado.`,
            `${n(p.resto)} con el ${p.baja} al lado.`,
          ],
        }
      }
      return {
        pregunta: `De ${n(p.producto)} a ${n(p.en)}, ¿cuánto queda de resto?`,
        explica: `De ${n(p.producto)} a ${n(p.en)} van ${n(p.resto)}.${p.resto === 0 ? ' Resto cero.' : ''}`,
        pistas: [
          `Es ${n(p.en)} menos ${n(p.producto)}.`,
          `Cuenta desde ${n(p.producto)} hasta ${n(p.en)}.`,
        ],
      }
    }

    case 'div-coma':
      return {
        explica: 'La siguiente cifra que bajo es decimal, así que pongo la coma en el cociente.',
        pistas: [],
      }

    case 'div-cero':
      return {
        explica: p.conComa
          ? 'No quedan cifras y sobra resto: pongo la coma en el cociente y bajo un cero.'
          : 'Sigue sobrando resto: bajo otro cero.',
        pistas: [],
      }

    case 'div-fin':
      return {
        explica:
          p.resto === 0
            ? `No quedan cifras que bajar y el resto es 0. El cociente es ${p.cociente}.`
            : `No quedan cifras que bajar. El cociente es ${p.cociente} y el resto ${n(p.resto)}.`,
        pistas: [],
      }

    case 'tabla':
      return {
        pregunta: `Tabla del ${n(p.tabla)}: ${n(p.tabla)} × ${n(p.por)}. ¿Cuánto?`,
        explica: `${n(p.tabla)} × ${n(p.por)} = ${n(p.resultado)}. Es ${n(p.tabla)} × ${n(p.por - 1)} = ${n(p.anterior)}, más ${n(p.tabla)}.`,
        pistas: [
          `Es la fila anterior de la tabla, más ${n(p.tabla)}.`,
          `${n(p.tabla)} × ${n(p.por - 1)} = ${n(p.anterior)}. Súmale ${n(p.tabla)}.`,
        ],
      }

    case 'traducir':
      return { explica: `${mayuscula(p.de)} es ${p.a}.`, pistas: [] }

    case 'producto':
      return {
        pregunta: `${n(p.a)} × ${n(p.b)}. ¿Cuánto?`,
        explica: `${n(p.a)} × ${n(p.b)} = ${n(p.resultado)}.`,
        pistas: [
          p.a <= 10
            ? `Tabla del ${n(p.a)}.`
            : `Hazlo en columna: cada cifra de ${n(p.a)} por ${n(p.b)}.`,
          `${n(p.a)} × ${n(p.b - 1)} = ${n(p.a * (p.b - 1))}, más ${n(p.a)}.`,
        ],
      }

    case 'mover-coma':
      return {
        pregunta: `Dividir entre 100 es mover la coma dos sitios a la izquierda. ${p.numero} : 100, ¿cuánto?`,
        explica: `Muevo la coma ${n(p.sitios)} sitios a la izquierda: ${p.numero} : 100 = ${p.resultado}.`,
        pistas: [
          `Si ${p.numero} acaba en dos ceros, quítalos. Si no, la coma se mueve dos sitios.`,
          `Las dos últimas cifras de ${p.numero} pasan detrás de la coma.`,
        ],
      }

    case 'coger':
      return {
        pregunta: `Cada parte son ${n(p.cada)}. Cojo ${n(p.partes)} partes: ${n(p.partes)} × ${n(p.cada)}. ¿Cuánto?`,
        explica: `Cada parte son ${n(p.cada)}, y cojo ${n(p.partes)}: ${n(p.partes)} × ${n(p.cada)} = ${n(p.resultado)}.`,
        pistas: [
          `${n(p.cada)} sumado ${n(p.partes)} veces.`,
          `Tabla del ${n(p.partes)}: ${n(p.partes)} × ${n(p.cada)}.`,
        ],
      }

    case 'primero': {
      const que =
        p.que === 'parentesis'
          ? 'Primero lo del paréntesis'
          : 'Primero la multiplicación, aunque vaya después'
      return {
        pregunta: `${que}: ${p.cuenta}. ¿Cuánto?`,
        explica: `${que}: ${p.cuenta} = ${n(p.resultado)}.`,
        pistas: [
          'Las multiplicaciones van antes que las sumas, salvo que haya paréntesis.',
          `Solo ${p.cuenta}, lo demás espera.`,
        ],
      }
    }

    case 'luego':
      return {
        pregunta: `Ahora lo demás: ${p.cuenta}. ¿Cuánto?`,
        explica: `Ahora lo demás: ${p.cuenta} = ${n(p.resultado)}.`,
        pistas: [`Ya solo queda una cuenta: ${p.cuenta}.`, `${p.cuenta}, sin más.`],
      }

    case 'subir': {
      const llega = p.resultado >= 0
      return {
        pregunta: `Estoy en ${n(p.desde)} y subo ${n(p.sube)}. ¿Dónde llego?`,
        explica: llega
          ? `Desde ${n(p.desde)} subo ${n(p.hastaCero)} hasta el 0, y me quedan ${n(p.sube - p.hastaCero)} por subir: llego a ${n(p.resultado)}.`
          : `Desde ${n(p.desde)} subo ${n(p.sube)} y no llego al 0: me quedo en ${n(p.resultado)}.`,
        pistas: [
          `Piensa en un termómetro: ${n(p.desde)} está bajo cero.`,
          llega
            ? `Hasta el 0 son ${n(p.hastaCero)}. Lo que sobre de ${n(p.sube)}, por encima.`
            : `Te faltan ${n(p.hastaCero)} para llegar al 0, y solo subes ${n(p.sube)}.`,
        ],
      }
    }

    case 'redondear':
      return {
        pregunta: `Redondeo ${p.numero} a la décima. Miro las centésimas: ${n(p.mira)}. ¿Cómo queda?`,
        explica: `Las centésimas son ${n(p.mira)}. ${
          p.sube
            ? 'Como es 5 o más, la décima sube una'
            : 'Como es menor que 5, la décima se queda igual'
        }: ${p.resultado}.`,
        pistas: [
          'Si la cifra que miras es 5 o más, la décima sube. Si no, se queda como está.',
          `${n(p.mira)} ${p.sube ? 'es 5 o más: la décima sube' : 'es menor que 5: la décima se queda'}.`,
        ],
      }
  }
}
